import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Mail,
  Plus,
  Send,
  RefreshCw,
  XCircle,
  Loader2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";

export default function AdminInvites() {
  const { authAxios } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    email: '',
    role: 'employee',
    department: '',
    message: ''
  });

  const fetchInvites = useCallback(async () => {
    try {
      const res = await authAxios.get('/invites');
      setInvites(res.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
      toast.error('Failed to load invites');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const resetForm = () => {
    setFormData({
      email: '',
      role: 'employee',
      department: '',
      message: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await authAxios.post('/invites', formData);
      toast.success(`Invite sent to ${formData.email}!`);
      setDialogOpen(false);
      resetForm();
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (invite) => {
    setProcessingId(invite.id);
    try {
      await authAxios.post(`/invites/${invite.id}/resend`);
      toast.success('Invite resent successfully!');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRevoke = async (invite) => {
    if (!window.confirm(`Revoke invite for ${invite.email}?`)) return;

    setProcessingId(invite.id);
    try {
      await authAxios.post(`/invites/${invite.id}/revoke`);
      toast.success('Invite revoked');
      fetchInvites();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invite');
    } finally {
      setProcessingId(null);
    }
  };

  const copyInviteLink = (invite) => {
    const appUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
    const link = `${appUrl}/register?token=${invite.token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      invited: 'bg-blue-100 text-blue-700 border-blue-200',
      used: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      expired: 'bg-stone-100 text-stone-600 border-stone-200',
      revoked: 'bg-rose-100 text-rose-700 border-rose-200'
    };
    const icons = {
      invited: <Clock className="w-3 h-3 mr-1" />,
      used: <CheckCircle2 className="w-3 h-3 mr-1" />,
      expired: <AlertCircle className="w-3 h-3 mr-1" />,
      revoked: <XCircle className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={`${styles[status]} flex items-center`}>
        {icons[status]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredInvites = invites.filter(invite => {
    if (activeTab === 'all') return true;
    return invite.status === activeTab;
  });

  const inviteCounts = {
    all: invites.length,
    invited: invites.filter(i => i.status === 'invited').length,
    used: invites.filter(i => i.status === 'used').length,
    expired: invites.filter(i => i.status === 'expired').length
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-invites">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Employee Invites</h1>
          <p className="text-stone-500 mt-1">Send and manage employee invitations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-600/20"
              data-testid="send-invite-btn"
            >
              <Send className="w-5 h-5 mr-2" />
              Send Invite
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-600" />
                Invite New Employee
              </DialogTitle>
              <DialogDescription>
                Send an invitation email for a new team member to register.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="employee@company.com"
                  className="bg-stone-50 border-stone-200"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="invite-role-select" className="bg-stone-50 border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="e.g., Operations, Construction"
                  className="bg-stone-50 border-stone-200"
                  data-testid="invite-department-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Personal Message (Optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Welcome to the team! We're excited to have you..."
                  className="bg-stone-50 border-stone-200"
                  rows={3}
                  data-testid="invite-message-input"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white w-full"
                  data-testid="confirm-send-invite-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-stone-100">
          <TabsTrigger value="all" className="data-[state=active]:bg-white">
            All ({inviteCounts.all})
          </TabsTrigger>
          <TabsTrigger value="invited" className="data-[state=active]:bg-white">
            Pending ({inviteCounts.invited})
          </TabsTrigger>
          <TabsTrigger value="used" className="data-[state=active]:bg-white">
            Used ({inviteCounts.used})
          </TabsTrigger>
          <TabsTrigger value="expired" className="data-[state=active]:bg-white">
            Expired ({inviteCounts.expired})
          </TabsTrigger>
        </TabsList>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-stone-400" />
              Invitations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredInvites.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No invitations found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-stone-100">
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Email</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Role</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Department</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Status</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Sent By</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Expires</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-stone-500 font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInvites.map((invite) => (
                      <TableRow key={invite.id} className="border-stone-100 hover:bg-stone-50">
                        <TableCell className="font-medium">{invite.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            invite.role === 'admin'
                              ? 'bg-purple-100 text-purple-800 border-purple-200'
                              : 'bg-blue-100 text-blue-800 border-blue-200'
                          }>
                            {invite.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-stone-600">{invite.department || '-'}</TableCell>
                        <TableCell>{getStatusBadge(invite.status)}</TableCell>
                        <TableCell className="text-stone-600">{invite.invited_by_name}</TableCell>
                        <TableCell className="text-stone-600">
                          {invite.status === 'invited' ? (
                            formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 justify-end">
                            {invite.status === 'invited' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => copyInviteLink(invite)}
                                  title="Copy invite link"
                                  className="hover:bg-stone-100"
                                  data-testid={`copy-link-${invite.id}`}
                                >
                                  <Copy className="w-4 h-4 text-stone-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleResend(invite)}
                                  disabled={processingId === invite.id}
                                  title="Resend invite"
                                  className="hover:bg-blue-50"
                                  data-testid={`resend-${invite.id}`}
                                >
                                  {processingId === invite.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <RefreshCw className="w-4 h-4 text-blue-600" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRevoke(invite)}
                                  disabled={processingId === invite.id}
                                  title="Revoke invite"
                                  className="hover:bg-rose-50"
                                  data-testid={`revoke-${invite.id}`}
                                >
                                  <XCircle className="w-4 h-4 text-rose-600" />
                                </Button>
                              </>
                            )}
                            {invite.status === 'expired' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResend(invite)}
                                disabled={processingId === invite.id}
                                className="text-blue-600 hover:bg-blue-50"
                                data-testid={`renew-${invite.id}`}
                              >
                                {processingId === invite.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                ) : (
                                  <RefreshCw className="w-4 h-4 mr-1" />
                                )}
                                Renew
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
